import {SubmittalMaker} from "../openings/SubmittalMaker";
import {KeyingSubmittal} from "../keying/submittal/KeyingSubmittal";
import {ProjectPageTitle} from "../ProjectPageTitle";
import React, {useEffect, useState} from "react";
import {ShopContainer} from "../ShopContainer";
import {useProjectId} from "../ProjectName";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {FormControlLabel, IconButton, Switch} from "@mui/material";
import {tableBorderColor} from "../hardware/library/ShortCodes";
import {Pending} from "./Pending";
import {ReloadContextProvider} from "../hardware/ReloadContext";
import {KeyingEditingContextProvider} from "./KeyingEditingContextProvider";
import {ShortCode} from "./ShortCode";
import {AddEditDesignation} from "./AddEditDesignation";
import AddCircle from "@mui/icons-material/AddCircle";
import {CreateShortCode} from "./CreateShortCode";
import {useSnackbar} from "../../../../misc/Snackbar";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {KeyingNotes} from "./KeyingNotes";
import {KeyingColumnSorting} from "./KeyingColumnSorting";
import {DragDropContext, Droppable, DroppableProvided} from "react-beautiful-dnd";
import {Keying2Label, Keying2Shortcode} from "../../../../api/Keying2";
import {KeyingLibrary} from "./KeyingLibrary";
import {MenuButton} from "../../../logistics/purchasing/MenuButton";
import {SmallGreyButton} from "../../../../misc/SmallGreyButton";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {CopyKeyingFromProject} from "./CopyKeyingFromProject";
import {LinkBaseBuildingProject} from "./LinkBaseBuildingProject";
import {BaseBuildingBanner} from "./BaseBuildingBanner";
import {PendingKeyedWarning} from "./PendingKeyedWarning";

export function Keying2() {
    const project = useProjectId();
    const snack = useSnackbar();

    const list = useAsync2(async (input) =>  api.keying2.list(input), {project: project}, [project]);

    const update = useAsyncAction(async (input) => {
        try {
            snack.loading("Loading...")
            await api.keying2.update(input);
            list.reload();
            snack.success("Updated");
        } catch (e: any) {
            snack.error(e.toString());
        }
    }, []);

    const applyLibrary = useAsyncAction(async (input: {
        project: number;
        labels: Keying2Label[];
        shortcodeNames: string[];
    }) => {
        try {
            snack.loading("Loading...")
            await api.keying2.applyLibrary(input);
            list.reload();
            snack.success("Updated");
        } catch (e: any) {
            snack.error(e.toString());
        }
    }, []);

    const listResult = list.result;

    // prevent flicker
    const [listCached, setListCached] = useState(listResult);

    useEffect(() => {
        if(!listResult) return;
        setListCached(listResult);
    }, [listResult]);

    const [showEditDesignation, setShowEditDesignation] = useState<string>();
    const labelsAvailable = listCached?.labelsAvailable || [];

    const [showLibrary, setShowLibrary] = useState(false);
    const [showCopyFrom, setShowCopyFrom] = useState(false);
    const [showLinkBaseBuilding, setShowLinkBaseBuilding] = useState(false);

    return (
        <ReloadContextProvider onReload={list.reload}>
            <ShopContainer name={
                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: 'center',
                    gap: 24,
                }}>
                    <div>
                        Keying
                    </div>
                    <div>
                        <FormControlLabel
                            control={<Switch size="small" checked={listCached?.enabled} onChange={(_, checked) => update.callback(Object.assign({}, listCached, {
                            enabled: checked
                        }))} />} label="Enabled" />
                    </div>
                </div>
            }
                           actions={<div style={{
                               display: "flex",
                               gap: 8,
                           }}>
                               <SubmittalMaker creator={KeyingSubmittal}  />
                               <KeyingNotes value={listResult?.quoteNotes || ""} />
                               <MenuButton
                                   renderButton={p => <SmallGreyButton endIcon={<ArrowDropDownIcon />} {...p} />}
                                   options={[
                                       {
                                           name: "Copy from Label Library",
                                           onClick: () => setShowLibrary(true),
                                       },
                                       {
                                           name: "Copy from Project",
                                           onClick: () => setShowCopyFrom(true),
                                       },
                                       {
                                           name: "Link Base Building",
                                           onClick: () => setShowLinkBaseBuilding(true),
                                       },
                                   ]}>Actions</MenuButton>
                           </div>}
            >
                <ProjectPageTitle title="Keying" />
                {list.error ? list.LoadingElement : null}
                {listCached && <BaseBuildingBanner value={listCached} />}
                <PendingKeyedWarning />

                    {listCached && showEditDesignation !== undefined && <AddEditDesignation name={showEditDesignation} value={listCached} onClose={() => setShowEditDesignation(undefined)} />}
                {showLibrary && <KeyingLibrary value={labelsAvailable} onCancel={() => {
                    setShowLibrary(false)
                }} onDone={(newLabels, shortcodeNames) => {
                    setShowLibrary(false)

                    if (newLabels.length === 0 && shortcodeNames.length === 0) return;

                    applyLibrary.callback({
                        project: project,
                        labels: newLabels,
                        shortcodeNames: shortcodeNames,
                    });
                }} />}
                {showCopyFrom && <CopyKeyingFromProject onCancel={() => setShowCopyFrom(false)} onDone={() => {
                    setShowCopyFrom(false);
                    list.reload();
                }} />}
                {showLinkBaseBuilding && <LinkBaseBuildingProject onCancel={() => setShowLinkBaseBuilding(false)} onDone={() => setShowLinkBaseBuilding(false)} />}
                    <KeyingEditingContextProvider>
                    <div style={{
                        flex: 1,
                        overflow: "auto",
                        borderTop: "1px solid " + grey["400"]
                    }}>
                        <table className={tableClass + " " + stickyHeaderTableClass} style={{
                            tableLayout: "fixed",
                        }}>
                            <colgroup>
                                <col style={{width: 300}}></col>
                                <col style={{width: 100}}></col>
                                {labelsAvailable.map(l => <col key={l.name} style={{width: 100}}></col>)}
                                <col style={{width: 100}}></col>
                                <col style={{width: 50}}></col>
                            </colgroup>
                            <thead className={theadCssName}>
                            <tr>
                                <td colSpan={labelsAvailable.length + 5} style={{padding: 0}}>
                                    <DragDropContext onDragEnd={(result) => {
                                        if (!result.destination) return;
                                        const dstIndex = result.destination.index;
                                        const srcIndex = result.source.index;

                                        const src = labelsAvailable[srcIndex];
                                        labelsAvailable.splice(srcIndex, 1);
                                        labelsAvailable.splice(dstIndex, 0, src);

                                        update.callback(Object.assign({}, listCached, {
                                            labelsAvailable: labelsAvailable,
                                        }));
                                    }}>
                                        <Droppable
                                            droppableId={"columns"}
                                            direction="horizontal"
                                        >
                                            {(
                                                dropProvided: DroppableProvided,
                                            ) => (
                                                <div
                                                    {...dropProvided.droppableProps}
                                                    ref={dropProvided.innerRef}
                                                    className={columnHeaderWrapperCss}
                                                >
                                                    <div style={{width: 300}} />
                                                    <div style={{width: 100, fontWeight: "500"}}>By Others</div>
                                                    <KeyingColumnSorting
                                                        labelsAvailable={labelsAvailable}
                                                        onShowEditDesignation={setShowEditDesignation}
                                                    />
                                                    {dropProvided.placeholder}
                                                    <div style={{width: 100}}>
                                                        <IconButton onClick={() => setShowEditDesignation("")}>
                                                            <AddCircle />
                                                        </IconButton>
                                                    </div>
                                                    <div style={{width: 50, fontWeight: "500"}}>Qty</div>
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </td>
                            </tr>
                            </thead>
                            <tbody>
                                {listCached?.shortcodes?.map(s => <ShortCode value={s} labels={listCached?.labelsAvailable || []} />)}
                                <CreateShortCode
                                    onShortcodes={search => {
                                        return listCached?.shortcodes?.filter(s => s.name.toLowerCase().includes(search.toLowerCase())) || [];
                                    }}
                                    labels={listCached?.labelsAvailable || []}
                                    project={project}
                                />
                                <Pending
                                    onShortcodes={(search, isOpening) => {
                                        return ((listCached?.shortcodes || []) as Keying2Shortcode[]).
                                            filter(s => {
                                                if(isOpening) {
                                                    return (s.miscItems?.length || 0) === 0
                                                }

                                                return (s.openings?.length || 0) === 0
                                            })
                                            .filter(s =>  s.name.toLowerCase().includes(search.toLowerCase()));
                                    }}
                                    labelCount={listCached?.labelsAvailable?.length || 0}
                                />
                            </tbody>
                        </table>
                    </div>
                    </KeyingEditingContextProvider>
            </ShopContainer>
        </ReloadContextProvider>
    );
}

export const columnCount = 5;

export const stickyHeaderTableClass = css({
    thead: {
        position: "sticky",
        top: -1,
        zIndex: 1,
        backgroundColor: "white",

        "& th": {
            position: "relative",
        },

        "& th:after": {
            content: "' '",
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 1,
            backgroundColor: grey["400"],
        }
    }
})

export const tableClass = css({
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",

    "& thead tr": {
        borderBottom: "1px solid " + tableBorderColor,
    },

    "& th": {
        fontSize: "0.8rem",
        paddingBottom: 2,
        paddingLeft: 4,
        paddingRight: 4,
        textAlign: "left",
    },
    "& td": {
        fontSize: "0.8rem",
        padding: 2,
        paddingLeft: 4,
        paddingRight: 4,
        overflow: "hidden",
        overflowWrap: "break-word",

        "&:last-child": {
            paddingRight: "12px",
        }
    },

    "& tr": {
        border: "1px solid transparent",
        borderBottom: "1px solid " + tableBorderColor,
    },

    "& td.no-pad": {
        padding: 0
    },
    "& td.multi-row-cell": {
        borderRight: "1px solid " + tableBorderColor,
        verticalAlign: "top",
    },
})

export const theadCssName = "keying2-thead";

const columnHeaderWrapperCss = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    borderBottom: "1px solid " + grey["400"],
    textAlign: "center",
})
